import {
  Table,
  Header,
  SpaceBetween,
  Pagination,
  Button,
  Popover,
} from "@amzn/awsui-components-react";
import { useState, useMemo } from "react";
import { COLUMN_DEFINITIONS } from "./ordersTableConfig";
import { generateRedriveExecutionUrl } from "../../../helpers/logUtils";
import { Stage } from "../../../lusca-search-api/generated-src";

interface OrderItem {
  orderId: string;
  stage: Stage;
}

interface OrdersTableProps {
  items: OrderItem[];
  loading: boolean;
  executionId: string;
  stage: Stage;
  user?: string;
  isTruncated?: boolean;
  downloadUrl?: string | null;
}

const PAGE_SIZE = 50;

export function OrdersTable({
  items,
  loading = false,
  executionId,
  stage,
  user,
  isTruncated = false,
  downloadUrl,
}: OrdersTableProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    // Orders were redriven in the execution's Dev stack.
    return items
      .slice(startIndex, endIndex)
      .map((item) => (user ? { ...item, user } : item));
  }, [items, currentPageIndex, user]);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);

  return (
    <Table
      resizableColumns
      stickyHeader
      columnDefinitions={COLUMN_DEFINITIONS}
      items={paginatedItems}
      loading={loading}
      loadingText={"Loading orders"}
      selectionType="multi"
      header={
        <Header
          variant="awsui-h1-sticky"
          counter={items.length > 0 ? `(${items.length})` : undefined}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {downloadUrl && (
                <Button
                  onClick={() => window.open(downloadUrl, "_blank")}
                  variant="normal"
                >
                  Download CSV
                </Button>
              )}
              <Button
                onClick={() =>
                  window.open(
                    generateRedriveExecutionUrl(stage, executionId, user),
                    "_blank"
                  )
                }
                variant="primary"
              >
                View in Console
              </Button>
            </SpaceBetween>
          }
          info={
            isTruncated ? (
              <span style={{ fontSize: "0.8em", color: "#666" }}>
                (
                <Popover
                  dismissButton={false}
                  position="bottom"
                  size="medium"
                  triggerType="custom"
                  content="Only the first 400KB of the orders file was loaded. The full file may contain additional orders. View in Console for complete list."
                >
                  <Button
                    iconName="status-warning"
                    variant="icon"
                    ariaLabel="Truncation warning"
                  />
                </Popover>
                truncated)
              </span>
            ) : undefined
          }
        >
          Orders in Redrive Execution {executionId}
        </Header>
      }
      empty={
        <SpaceBetween size="m" alignItems="center">
          <div>No orders found for this execution</div>
        </SpaceBetween>
      }
      pagination={
        items.length > PAGE_SIZE ? (
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        ) : undefined
      }
    />
  );
}
